import { useSearchParams } from 'react-router';
import type { DeliveryImpactParams } from '../../../api/agent-miner-client/agent-miner-client.type';
import { useModelComparisonParams } from '../model-comparison/model-comparison.hooks';
import type { DeliveryImpactView, DeliveryTrendView } from './delivery-impact.type';

/**
 * Resolves the delivery impact query params. The endpoint takes exactly the
 * Model Comparison scope (organization, shared URL date window, effective
 * allocation type), so this re-uses that tab's param resolution — the Usage
 * tab sets the same precedent — and the date range carries across tabs.
 *
 * @returns {DeliveryImpactParams} Query params for useDeliveryImpact
 */
const useDeliveryImpactParams = (): DeliveryImpactParams => useModelComparisonParams();

/**
 * Tiles/trend view mode for the tab, kept in the `di_view` URL param so it
 * survives navigation like the rest of the page's filter state. Tiles is the
 * default and is encoded as param absence to keep shared URLs clean; an
 * unknown hand-edited value falls back to tiles.
 *
 * @returns {{ view: DeliveryImpactView, setView: (view: DeliveryImpactView) => void }}
 */
const useDeliveryImpactView = (): {
  view: DeliveryImpactView;
  setView: (view: DeliveryImpactView) => void;
} => {
  const [searchParams, setSearchParams] = useSearchParams();
  const raw = searchParams.get('di_view');
  const view: DeliveryImpactView = raw === 'trend' ? 'trend' : 'tiles';

  const setView = (next: DeliveryImpactView) => {
    const copy = new URLSearchParams(searchParams);
    if (next === 'tiles') copy.delete('di_view');
    else copy.set('di_view', next);
    setSearchParams(copy, { replace: true });
  };

  return { view, setView };
};

/**
 * URL-synced line/bar toggle for the Delivery Impact trend chart. It is
 * deliberately independent from `di_view`, which chooses tiles vs trend in
 * the filter panel. Line is the default and therefore omitted from the URL.
 *
 * @returns {{ trendView: DeliveryTrendView, setTrendView: (view: DeliveryTrendView) => void }}
 */
const useDeliveryTrendView = (): {
  trendView: DeliveryTrendView;
  setTrendView: (view: DeliveryTrendView) => void;
} => {
  const [searchParams, setSearchParams] = useSearchParams();
  const trendView: DeliveryTrendView = searchParams.get('di_chart_view') === 'bar' ? 'bar' : 'line';

  const setTrendView = (next: DeliveryTrendView) => {
    const copy = new URLSearchParams(searchParams);
    if (next === 'line') copy.delete('di_chart_view');
    else copy.set('di_chart_view', next);
    setSearchParams(copy, { replace: true });
  };

  return { trendView, setTrendView };
};

/**
 * URL-synced open/closed state of the AI-sessions drill-down side panel, held
 * in the `di_panel` param so the drill-down survives a refresh and is
 * shareable like the rest of the tab's state (mirrors `sessions_family` on
 * Model Comparison). Closed is encoded as param absence.
 *
 * @returns {{ isPanelOpen: boolean, setPanelOpen: (open: boolean) => void }}
 */
const useDeliveryImpactDrilldown = (): {
  isPanelOpen: boolean;
  setPanelOpen: (open: boolean) => void;
} => {
  const [searchParams, setSearchParams] = useSearchParams();
  const isPanelOpen = searchParams.get('di_panel') === 'ai';

  const setPanelOpen = (open: boolean) => {
    const copy = new URLSearchParams(searchParams);
    if (open) copy.set('di_panel', 'ai');
    else copy.delete('di_panel');
    setSearchParams(copy, { replace: true });
  };

  return { isPanelOpen, setPanelOpen };
};

export { useDeliveryImpactDrilldown, useDeliveryImpactParams, useDeliveryImpactView, useDeliveryTrendView };
